import React, { useEffect, useState, useRef } from 'react';
import SeoHelmet from '../components/SeoHelmet';
import { seo } from '../config/seo';
import { getCameraPics } from '../functions/getCameraPics';
import { getCameraVisibility } from '../functions/getCameraVisibility';
import { trackEvent } from '../functions/analytics';
import '../styles/cameras.css';


const Cameras = () => {
  const [cameraPics, setCameraPics] = useState([]);
  const [imgCamera, setImgCamera] = useState();
  const [hidden, setHidden] = useState(false);
  const [hiddenMessage, setHiddenMessage] = useState('');
  const currentImageFilenameRef = useRef(null);

  const fetchCameraData = async () => {
    try {
      // Admin can hide the camera from the dashboard - check that before loading a frame.
      const visibility = await getCameraVisibility();
      if (visibility && visibility.hidden) {
        setHidden(true);
        setHiddenMessage(visibility.message || '');
        setImgCamera((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return undefined;
        });
        currentImageFilenameRef.current = null;
        return;
      }
      setHidden(false);

      const cameraData = await getCameraPics();
      setCameraPics(cameraData);

      if (!cameraData || cameraData.length === 0) {
        console.warn('No camera data available.');
        return;
      }

      const newImageFilename = cameraData[0];
      
      // Only update if the image filename has changed
      if (currentImageFilenameRef.current === newImageFilename) {
        console.log('Image unchanged, skipping update:', newImageFilename);
        return;
      }

      console.log('New image detected, updating:', newImageFilename);

      const url = process.env.REACT_APP_API_HOST + '/static/webp/' + newImageFilename;
      console.log('Fetching image from:', url);

      const res = await fetch(url);
      if (!res.ok) {
        console.error(`Failed to fetch image: ${res.status} ${res.statusText}`, url);
        console.error('Response headers:', Object.fromEntries(res.headers.entries()));
        throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
      }

      // Check content length from headers
      const contentLength = res.headers.get('content-length');
      console.log('Content-Length header:', contentLength);
      
      if (contentLength === '0' || (contentLength && parseInt(contentLength) === 0)) {
        console.warn('Server returned empty file (Content-Length: 0) for:', newImageFilename);
        return;
      }

      const imageBlob = await res.blob();
      console.log('Received blob, size:', imageBlob.size, 'type:', imageBlob.type);
      
      // Check if blob is empty or invalid
      if (!imageBlob || imageBlob.size === 0) {
        console.warn('Received empty blob for image:', newImageFilename, 'URL:', url);
        console.warn('This might mean the webp file does not exist or conversion failed');
        return;
      }
      
      // Check if blob type is correct
      if (!imageBlob.type.startsWith('image/')) {
        console.warn('Received non-image blob:', imageBlob.type, 'for file:', newImageFilename);
        return;
      }

      // Clean up previous object URL to prevent memory leaks
      if (imgCamera) {
        URL.revokeObjectURL(imgCamera);
      }

      const imageObjectURL = URL.createObjectURL(imageBlob);
      setImgCamera(imageObjectURL);
      currentImageFilenameRef.current = newImageFilename;

      console.log('Image loaded successfully, size:', imageBlob.size, 'bytes');
    } catch (error) {
      console.error('Error fetching image:', error);
      // Handle errors, e.g., retry or display a placeholder image
    }
  };



  useEffect(() => {
    const loadData = async () => {
      await fetchCameraData();
    };

    // Initial load
    loadData();

    // Cameras refresh every 10 seconds
    const intervalId = setInterval(loadData, 10000);

    // Cleanup the interval and object URL when the component is unmounted
    return () => {
      clearInterval(intervalId);
      if (imgCamera) {
        URL.revokeObjectURL(imgCamera);
      }
    };
  }, []);

  return (
    <div className="kamery">
      <SeoHelmet title={seo.pages.cameras.title} description={seo.pages.cameras.description} path="/cameras" />
      <h1 style={{ color: 'white', textAlign: 'center' }}>Záznamy kamier</h1>
      {!hidden && (
        <p style={{ color: 'white', textAlign: 'center' }}>
          Ak záznamy preblikávajú, poprípade nie sú vidno, načítavajú sa. Prosím, počkajte!
        </p>
      )}
      {hidden ? (
        <p className="camera-hidden-note">
          {hiddenMessage || 'Záznam z kamery je momentálne nedostupný.'}
        </p>
      ) : (
        <img
          src={imgCamera}
          alt="Camera"
          onClick={() => trackEvent('camera_click', { event_category: 'camera', event_label: 'cameras_page_image' })}
        />
      )}
    </div>
  );
};

export default Cameras;
